import React, { useState, useEffect, useContext } from 'react';
import { Drawer, Steps, Tag, Typography, Avatar, Divider, Space, Modal, Popconfirm, Button, message, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
    CheckCircleOutlined,
    SolutionOutlined,
    GiftOutlined,
    CaretDownOutlined,
    ClockCircleOutlined,
    DeleteOutlined,
    EyeOutlined,
    CalendarOutlined,
    UserOutlined,
    PhoneOutlined,
} from '@ant-design/icons';
import { IconBike, IconAlertCircle } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { usePreference } from '../context/PreferenceContext';
import { useDeliveryEstimate } from '../hooks/useDeliveryEstimate';
import { LocationContext } from '../LocationContext';
import { formatDateTime } from "../util/dateUtils";
import SafeImage from "./SafeImage";

const { Text } = Typography;

const DeliveryTimer = ({ createdAt, status, shopId, onDelete, onReschedule, deliveryType, scheduledDate, scheduledTime, estimatedDeliveryTime }) => {
    const { latitude, longitude } = useContext(LocationContext);
    const { estimates, loading } = useDeliveryEstimate(shopId, latitude, longitude);
    const [timeLeft, setTimeLeft] = useState(null);
    const [timerColor, setTimerColor] = useState('#52c41a'); // Green
    const [isTimeUp, setIsTimeUp] = useState(false);
    const [expectedTime, setExpectedTime] = useState(null);
    const [isScheduledFuture, setIsScheduledFuture] = useState(false);

    useEffect(() => {
        if (['delivered', 'cancelled'].includes(status?.toLowerCase())) {
            setTimeLeft(null);
            setIsTimeUp(false);
            return;
        }

        setIsTimeUp(false);

        let targetTime;
        const now = dayjs();

        // Priority 1: Use scheduledDate if available
        if (scheduledDate) {
            const sDate = dayjs(scheduledDate);
            let sTime = scheduledTime || "00:00";

            if (sTime.includes(' - ')) {
                sTime = sTime.split(' - ')[1];
            }

            let hour, minute;
            if (sTime.includes('AM') || sTime.includes('PM')) {
                const timeMatch = sTime.match(/(\d+):(\d+)\s*(AM|PM)/i);
                if (timeMatch) {
                    hour = parseInt(timeMatch[1]);
                    minute = parseInt(timeMatch[2]);
                    const ampm = timeMatch[3].toUpperCase();
                    if (ampm === 'PM' && hour < 12) hour += 12;
                    if (ampm === 'AM' && hour === 12) hour = 0;
                } else {
                    hour = 0; minute = 0;
                }
            } else {
                const [h, m] = sTime.split(':').map(Number);
                hour = h || 0;
                minute = m || 0;
            }

            targetTime = dayjs(scheduledDate).set('hour', hour).set('minute', minute).set('second', 0).set('millisecond', 0);

            if (sDate.isAfter(now, 'day')) {
                setIsScheduledFuture(true);
                setExpectedTime(targetTime);
                return;
            }
        }

        // Priority 2: Use Real-time Estimate from hook
        if (!targetTime) {
            const est = estimates[shopId?.toString()];
            if (est && est.eta) {
                targetTime = dayjs(est.eta);
            }
        }

        // Priority 3: Use Saved Estimated Delivery Time from Order
        if (!targetTime && estimatedDeliveryTime) {
            const numbers = String(estimatedDeliveryTime).match(/\d+/g);
            if (numbers && numbers.length > 0) {
                // Handle "1 hr 54 mins" by converting to total minutes
                let totalMins = 0;
                if (String(estimatedDeliveryTime).toLowerCase().includes('hr') && numbers.length >= 2) {
                    totalMins = (parseInt(numbers[0]) * 60) + parseInt(numbers[1]);
                } else {
                    totalMins = parseInt(numbers[0]);
                }
                targetTime = dayjs(createdAt).add(totalMins, 'minute');
            }
        }

        // Fallback: 30 minutes
        if (!targetTime) {
            targetTime = dayjs(createdAt).add(30, 'minute');
        }

        setExpectedTime(targetTime);
        setIsScheduledFuture(false);

        const updateTimer = () => {
            const currentTime = dayjs();
            const diff = targetTime.diff(currentTime);

            if (diff <= 0) {
                const overDiff = Math.abs(diff);
                const overHours = Math.floor(overDiff / 3600000);
                const overMinutes = Math.floor((overDiff % 3600000) / 60000);

                if (overHours > 0) {
                    setTimeLeft(`${overHours}h ${overMinutes}m`);
                } else {
                    setTimeLeft(`${overMinutes}m`);
                }

                setTimerColor('#ff4d4f'); // Red
                setIsTimeUp(true);
            } else {
                const hours = Math.floor(diff / 3600000);
                const totalMinutes = Math.floor(diff / 60000);
                const minutes = Math.floor((diff % 3600000) / 60000);
                const seconds = Math.floor((diff % 60000) / 1000);

                if (hours > 0) {
                    setTimeLeft(`${hours}h ${minutes}m`);
                } else {
                    if (totalMinutes >= 1) {
                        setTimeLeft(`${String(totalMinutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
                    } else {
                        setTimeLeft(`${seconds}s`);
                    }
                }

                setIsTimeUp(false);
                if (diff <= 5 * 60 * 1000) {
                    setTimerColor('#ff4d4f'); // Red
                } else {
                    setTimerColor('#52c41a'); // Green
                }
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [createdAt, status, estimates, shopId, deliveryType, scheduledDate, scheduledTime]);

    if (isScheduledFuture) {
        return (
            <div className="pcu-flex pcu-flex-column pcu-align-end">
                <div className="pcu-flex pcu-flex-center-y pcu-gap-6 pcu-font-11 pcu-font-800" style={{ color: 'var(--nearzon-purple)' }}>
                    <ClockCircleOutlined className="pcu-font-12" />
                    <span>Scheduled</span>
                </div>
                {expectedTime && (
                    <div className="pcu-font-9 pcu-color-muted pcu-margin-top-2 pcu-font-700 pcu-text-uppercase">
                        for {expectedTime.format('DD MMM, h:mm A')}
                    </div>
                )}
            </div>
        );
    }

    if (!timeLeft && !loading) return null;
    if (loading && !timeLeft) return <div className="pcu-font-11 pcu-color-muted">Estimating...</div>;

    return (
        <div className="pcu-flex pcu-flex-center-y pcu-gap-8">
            <div className="pcu-flex pcu-flex-column pcu-align-end">
                <div
                    className="pcu-flex pcu-flex-center-y pcu-gap-6 pcu-padding-y-4 pcu-padding-x-10 pcu-radius-20 pcu-transition-all"
                    style={{
                        background: isTimeUp ? 'rgba(255, 77, 79, 0.08)' : 'rgba(82, 196, 26, 0.08)',
                        color: timerColor,
                        border: `1px solid ${isTimeUp ? 'rgba(255, 77, 79, 0.15)' : 'rgba(82, 196, 26, 0.15)'}`
                    }}
                >
                    <ClockCircleOutlined className="pcu-font-12" />
                    <span className="pcu-font-12 pcu-font-800 pcu-letter-spacing-1" style={{ whiteSpace: 'nowrap' }}>
                        {isTimeUp ? `DELAYED ${timeLeft}` : `IN ${timeLeft}`}
                    </span>
                </div>
                {expectedTime && (
                    <div className="pcu-font-10 pcu-color-muted pcu-margin-top-4 pcu-font-600 pcu-opacity-8">
                        {expectedTime.format('h:mm A')}
                    </div>
                )}
            </div>
            {isTimeUp && (
                <div className="pcu-flex pcu-gap-4 pcu-margin-left-6" style={{ marginTop: 2 }}>
                    {onReschedule && (
                        <Tooltip title="Reschedule">
                            <Button
                                type="text"
                                size="small"
                                onClick={onReschedule}
                                className="pcu-flex-center pcu-radius-8 pcu-color-purple"
                                style={{ width: 28, height: 28, opacity: 0.7 }}
                                icon={<ClockCircleOutlined style={{ fontSize: 16 }} />}
                            />
                        </Tooltip>
                    )}
                    {onDelete && (
                        <Popconfirm
                            title="Cancel Delayed Order?"
                            onConfirm={onDelete}
                            okText="Yes"
                            cancelText="No"
                            okButtonProps={{ danger: true }}
                        >
                            <Tooltip title="Cancel Order">
                                <Button
                                    type="text"
                                    danger
                                    size="small"
                                    className="pcu-flex-center pcu-radius-8"
                                    style={{ width: 28, height: 28, opacity: 0.7 }}
                                    icon={<DeleteOutlined style={{ fontSize: 16 }} />}
                                />
                            </Tooltip>
                        </Popconfirm>
                    )}
                </div>
            )}
        </div>
    );
};

const PackingTimer = ({ confirmedHistory }) => {
    const [timeLeft, setTimeLeft] = useState('');
    const [isLate, setIsLate] = useState(false);

    useEffect(() => {
        if (!confirmedHistory || !confirmedHistory.timestamp) return;

        const targetTime = dayjs(confirmedHistory.timestamp).add(5, 'minute');

        const calculateTimeLeft = () => {
            const now = dayjs();
            const diffSeconds = targetTime.diff(now, 'second');

            if (diffSeconds <= 0) {
                setTimeLeft('Auto-packing...');
                setIsLate(true);
            } else {
                const mins = Math.floor(diffSeconds / 60);
                const secs = diffSeconds % 60;
                setTimeLeft(`${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`);
                setIsLate(diffSeconds < 60);
            }
        };

        calculateTimeLeft();
        const timer = setInterval(calculateTimeLeft, 1000);
        return () => clearInterval(timer);
    }, [confirmedHistory]);

    if (!confirmedHistory) return null;

    return (
        <div className={`pcu-flex pcu-flex-center-y pcu-gap-8 pcu-padding-x-12 pcu-padding-y-8 pcu-radius-10 pcu-margin-bottom-12 ${isLate ? 'pcu-bg-red-light' : 'pcu-bg-orange-light'}`} style={{ border: `1px solid ${isLate ? '#fca5a5' : '#fed7aa'}` }}>
            <ClockCircleOutlined style={{ fontSize: 16, color: isLate ? '#dc2626' : '#d97706' }} />
            <div className="pcu-flex pcu-flex-column">
                <Text className={`pcu-font-10 pcu-font-700 pcu-text-uppercase pcu-letter-spacing-1 ${isLate ? 'pcu-color-red' : 'pcu-color-orange'}`}>
                    Packing Countdown
                </Text>
                <Text className={`pcu-font-14 pcu-font-800 ${isLate ? 'pcu-color-red' : 'pcu-color-orange'}`}>
                    {timeLeft}
                </Text>
            </div>
        </div>
    );
};

const OrderStatusDrawer = ({ open, onClose, orders, isSeller, onStatusUpdate, onConfirmReplaced, onReschedule, onCancel, onRequestUpdate, onViewDetails, onIssueIconClick }) => {
    const { t, formatPrice: fmtPrice } = usePreference();
    const navigate = useNavigate();
    const [expandedOrderId, setExpandedOrderId] = useState(null);

    const activeOrders = Array.isArray(orders) ? orders : (orders ? [orders] : []);

    const getStatusStep = (order) => {
        const s = order.status?.toLowerCase();

        if (order?.productIssueRequest?.status === 'ACCEPTED') {
            if (s === 'delivered') return 3; // Issue Request timeline has 4 steps (0,1,2,3)
            return 2; // "Order Updated" step
        }

        if (s === 'ordered') return 0;
        if (s === 'confirmed' || s === 'assigned') return 1;
        if (s === 'packed') return 2;
        if (s === 'picked_up' || s === 'shipped') return 3;
        if (s === 'delivered') return 4;
        return 0;
    };

    const getStatusLabel = (order) => {
        const status = order.status;
        if (order?.productIssueRequest?.status === 'ACCEPTED' && ['ordered', 'confirmed', 'packed'].includes(status?.toLowerCase())) {
            return 'Order Updated';
        }

        const map = {
            ordered: 'Ordered',
            confirmed: 'Confirmed',
            assigned: 'Assigned',
            packed: 'Packed',
            picked_up: 'Picked Up',
            shipped: 'Out for Delivery',
            out_for_delivery: 'Out for Delivery',
            delivered: 'Delivered',
            awaiting_buyer_approval: 'Issue Reported',
            replaced: 'Replaced Order',
        };
        return map[status?.toLowerCase()] || status?.replace(/_/g, ' ');
    };

    if (activeOrders.length === 0 && open) {
        // If drawer open but no active orders (e.g. all delivered), close it
        setTimeout(() => onClose(), 500);
    }

    return (
        <Drawer
            title={
                <div className="pcu-flex-between pcu-flex-center-y pcu-padding-right-16">
                    <Text className="pcu-font-16 pcu-font-800 pcu-color-purple">{t('active_tracking', 'Active Tracking')}</Text>
                    <div className="pcu-flex pcu-flex-center-y pcu-gap-8">
                        <div className="pcu-font-10 pcu-font-700 pcu-color-muted pcu-letter-spacing-1">In Progress</div>
                        <Tag color="purple" className="pcu-radius-20 pcu-font-11 pcu-font-700 pcu-margin-0 pcu-border-none px-12" style={{ background: 'var(--color-bg-muted)', color: 'var(--nearzon-purple)' }}>
                            {activeOrders.length} {activeOrders.length === 1 ? 'Order' : 'Orders'}
                        </Tag>
                    </div>
                </div>
            }
            placement="right"
            onClose={onClose}
            open={open}
            width={600}
            styles={{
                body: { padding: '12px 0 24px 0' },
                header: { borderBottom: '1px solid var(--color-border-primary)', padding: '20px 24px' }
            }}
        >
            <div className="drawer-active-orders-list">
                {activeOrders.map((order, index) => {
                    const shopId = order.shopId?._id || order.shopId || (order.items?.[0]?.product?.shopId?._id || order.items?.[0]?.product?.shopId);
                    const bgColors = ['#F8FAFC', '#FFF7ED', '#F0FDF4', '#EFF6FF', '#FDF2F8']; // Subtle slate, orange, green, blue, pink
                    const cardBg = isSeller ? '#ffffff' : bgColors[index % bgColors.length];

                    return (
                        <div key={order._id} className="drawer-order-group" style={{ margin: '16px 20px', borderRadius: '20px', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 12px rgba(0,0,0,0.03)', background: cardBg, overflow: 'hidden' }}>
                            <div className="drawer-order-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', background: 'rgba(255,255,255,0.4)', padding: '18px 20px', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                                <div className="pcu-flex-column pcu-gap-8" style={{ flex: 1 }}>
                                    <div className="pcu-flex pcu-flex-center-y pcu-gap-10" style={{ flexWrap: 'nowrap' }}>
                                        <Text className="pcu-font-16 pcu-font-900 pcu-color-heading pcu-letter-spacing-1" style={{ whiteSpace: 'nowrap' }}>#{order.orderId}</Text>
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<EyeOutlined style={{ fontSize: 13 }} />}
                                            onClick={() => {
                                                if (onViewDetails) {
                                                    onViewDetails(order);
                                                } else {
                                                    onClose();
                                                    setTimeout(() => {
                                                        navigate('/buyer/orders', { state: { highlightOrderId: order._id } });
                                                    }, 300);
                                                }
                                            }}
                                            className="pcu-flex-center pcu-radius-12 pcu-font-700 pcu-font-11"
                                            style={{
                                                color: '#FF8800',
                                                background: 'rgba(255,136,0,0.1)',
                                                padding: '4px 12px',
                                                height: 'auto',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            View Details
                                        </Button>
                                    </div>
                                    {!isSeller && (
                                        <div className="pcu-font-12 pcu-font-600 pcu-color-muted pcu-flex pcu-flex-center-y pcu-gap-6" style={{ marginTop: -2 }}>
                                            {order.scheduledDate ? (
                                                <span className="pcu-flex pcu-flex-center-y pcu-gap-4 pcu-padding-x-8 pcu-padding-y-4 pcu-radius-12" style={{ background: 'rgba(255,136,0,0.08)', color: 'var(--nearzon-orange)' }}>
                                                    <CalendarOutlined style={{ fontSize: 13 }} />
                                                    <span>Scheduled: {dayjs(order.scheduledDate).format('DD MMM')}</span>
                                                </span>
                                            ) : (
                                                <span className="pcu-flex pcu-flex-center-y pcu-gap-6 pcu-opacity-7">
                                                    <ClockCircleOutlined style={{ fontSize: 13 }} />
                                                    <span>Placed: {dayjs(order.createdAt).format('DD MMM, h:mm A')}</span>
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {!isSeller && (
                                    <div className="pcu-flex pcu-flex-center-y pcu-margin-left-12">
                                        <DeliveryTimer
                                            createdAt={order.createdAt}
                                            status={order.status}
                                            shopId={shopId}
                                            onDelete={() => onStatusUpdate && onStatusUpdate(order._id, 'cancelled')}
                                            onReschedule={() => onReschedule && onReschedule(order)}
                                            deliveryType={order.deliveryType}
                                            scheduledDate={order.scheduledDate}
                                            scheduledTime={order.scheduledTime}
                                            estimatedDeliveryTime={order.estimatedDeliveryTime}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="pcu-margin-x-20 pcu-margin-top-12 pcu-flex pcu-items-center pcu-gap-8" style={{ justifyContent: 'flex-end' }}>
                                {/* Issue Reported Action — only for awaiting_buyer_approval */}
                                {order.status?.toLowerCase() === 'awaiting_buyer_approval' && (
                                    <div className="pcu-flex pcu-items-center pcu-gap-8 pcu-margin-right-8">
                                        <button
                                            className="issue-reported-icon-btn"
                                            title="View Reported Issue"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (onIssueIconClick) onIssueIconClick(order);
                                            }}
                                        >
                                            <IconAlertCircle size={14} />
                                            <span className="issue-reported-icon-label">Resolve Issue</span>
                                        </button>
                                    </div>
                                )}

                                <div
                                    className={`drawer-status-trigger status-tint-${order.status?.toLowerCase()} pcu-cursor-pointer`}
                                    style={{ width: 'auto', gap: '8px' }}
                                    onClick={() => setExpandedOrderId(expandedOrderId === order._id ? null : order._id)}
                                >
                                    <span className={`status-dot dot-${order.status?.toLowerCase()} pcu-w-6 pcu-h-6 pcu-radius-full`}></span>
                                    <span className="pcu-font-700">
                                        {order.status?.toLowerCase() === 'awaiting_buyer_approval' ? 'ISSUE REPORTED' : getStatusLabel(order)}
                                    </span>
                                    <CaretDownOutlined className="pcu-font-10 pcu-transition-all" style={{ transform: expandedOrderId === order._id ? 'rotate(180deg)' : 'none' }} />
                                </div>
                            </div>



                            {order.currentAssignedStaff && (
                                <div className="pcu-margin-x-20 pcu-margin-bottom-16 pcu-padding-16 pcu-radius-12 pcu-bg-slate-50 pcu-border-slate-100 pcu-flex pcu-items-center pcu-gap-12">
                                    <Avatar
                                        size={40}
                                        icon={<SolutionOutlined />}
                                        src={order.currentAssignedStaff.profilePicture}
                                        style={{ backgroundColor: 'var(--nearzon-purple)' }}
                                    />
                                    <div className="pcu-flex-column">
                                        <Text className="pcu-font-10 pcu-font-700 pcu-color-muted pcu-text-uppercase pcu-letter-spacing-1">Assigned Delivery Partner</Text>
                                        <Text strong className="pcu-font-14 pcu-color-title">
                                            {order.currentAssignedStaff.firstName} {order.currentAssignedStaff.lastName}
                                        </Text>
                                        <Text type="secondary" className="pcu-font-12">
                                            {order.currentAssignedStaff.phone || order.currentAssignedStaff.mobile}
                                        </Text>
                                    </div>
                                    <div className="pcu-flex-grow-1" />
                                    <Button
                                        type="primary"
                                        shape="circle"
                                        size="small"
                                        icon={<IconBike style={{ fontSize: 12 }} />}
                                        className="pcu-bg-purple pcu-border-none"
                                        onClick={() => window.open(`tel:${order.currentAssignedStaff.phone || order.currentAssignedStaff.mobile}`)}
                                    />
                                </div>
                            )}

                            {expandedOrderId === order._id && (
                                <div className="drawer-tracking-timeline pcu-padding-x-32 pcu-padding-y-20 pcu-bg-muted pcu-margin-x-20 pcu-margin-top-12 pcu-radius-12"
                                    style={{
                                        display: "flex",
                                        justifyContent: "flex-end"
                                    }}>
                                    <Steps
                                        direction="vertical"
                                        size="small"
                                        current={getStatusStep(order)}
                                        items={order.productIssueRequest?.status === 'ACCEPTED' ? [
                                            {
                                                title: 'Issue Request',
                                                description: dayjs(order.productIssueRequest.requestedAt).format('h:mm A'),
                                                icon: <IconAlertCircle className="pcu-font-12" />
                                            },
                                            {
                                                title: 'Buyer Approved',
                                                description: 'Changes Accepted',
                                                icon: <CheckCircleOutlined className="pcu-font-12" />
                                            },
                                            {
                                                title: 'Order Updated',
                                                description: 'Successfully',
                                                icon: <SolutionOutlined className="pcu-font-12" />
                                            },
                                            {
                                                title: 'Delivered',
                                                description: order.statusHistory?.find(h => h.status === 'delivered') ? dayjs(order.statusHistory.find(h => h.status === 'delivered').timestamp).format('h:mm A') : 'Completed',
                                                icon: <CheckCircleOutlined className="pcu-font-12" />
                                            }
                                        ] : [
                                            {
                                                title: 'Ordered',
                                                description: formatDateTime(order.createdAt),
                                                icon: <SolutionOutlined className="pcu-font-12" />
                                            },
                                            {
                                                title: 'Confirmed',
                                                description: order.statusHistory?.find(h => h.status === 'confirmed') ? dayjs(order.statusHistory.find(h => h.status === 'confirmed').timestamp).format('h:mm A') : 'Awaiting',
                                                icon: <CheckCircleOutlined className="pcu-font-12" />
                                            },
                                            {
                                                title: 'Packed',
                                                description: order.statusHistory?.find(h => h.status === 'packed') ? dayjs(order.statusHistory.find(h => h.status === 'packed').timestamp).format('h:mm A') : 'In progress',
                                                icon: <GiftOutlined className="pcu-font-12" />
                                            },
                                            {
                                                title: 'Picked Up',
                                                description: order.statusHistory?.find(h => (h.status === 'picked_up' || h.status === 'Picked_Up' || h.status === 'out_for_delivery' || h.status === 'shipped')) ? dayjs(order.statusHistory.find(h => (h.status === 'picked_up' || h.status === 'Picked_Up' || h.status === 'out_for_delivery' || h.status === 'shipped')).timestamp).format('h:mm A') : 'Soon',
                                                icon: <IconBike className="pcu-font-12" />
                                            },
                                            {
                                                title: 'Delivered',
                                                description: order.statusHistory?.find(h => h.status === 'delivered') ? dayjs(order.statusHistory.find(h => h.status === 'delivered').timestamp).format('h:mm A') : 'Completed',
                                                icon: <CheckCircleOutlined className="pcu-font-12" />
                                            },
                                        ]}
                                    />
                                </div>
                            )}

                            {order.items?.map((item, idx) => {
                                const isItemCancelled = item.quantity === 0 || item.status === 'Cancelled';
                                const displayPrice = isItemCancelled ? 0 : (item.price * (item.quantity || 1));
                                const itemOpacity = isItemCancelled ? 0.5 : 1;
                                const textDecoration = isItemCancelled ? "line-through" : "none";

                                return (
                                    <div key={`${order._id}-${idx}`} className="drawer-product-item" style={{ opacity: itemOpacity }}>
                                        <div className="pcu-padding-20 pcu-flex pcu-gap-16">
                                            <SafeImage
                                                src={item.product?.imageUrl || (item.product?.images && item.product?.images[0]) || item.imageUrl || item.image}
                                                fallbackSrc={item.imageUrl || item.image}
                                                className="pcu-radius-12 pcu-border-slate"
                                                style={{ width: 64, height: 64, objectFit: "cover", flexShrink: 0 }}
                                            />
                                            <div className="pcu-flex-grow-1 pcu-min-w-0">
                                                <div className="pcu-flex-between pcu-items-start pcu-gap-12" style={{ marginTop: "12px" }}>
                                                    <div className="pcu-flex pcu-flex-column pcu-flex-grow-1">
                                                        <Text className="pcu-font-14 pcu-font-700 pcu-line-clamp-2 pcu-line-height-1-4 pcu-color-title" style={{ textDecoration }}>
                                                            {item.name || item.product?.name}
                                                        </Text>
                                                        {isItemCancelled && (
                                                            <div style={{ color: "#ef4444", fontSize: 10, fontWeight: "bold", background: "#fef2f2", padding: "2px 6px", borderRadius: 4, display: "inline-block", marginTop: 4, alignSelf: "flex-start" }}>
                                                                Removed / Cancelled
                                                            </div>
                                                        )}
                                                        {(item.unit || item.weight) && (
                                                            <Text className="pcu-font-12 pcu-color-muted pcu-margin-top-2">
                                                                {item.unit || item.weight}
                                                            </Text>
                                                        )}
                                                        {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                                                            <div className="pcu-font-11 pcu-color-muted pcu-margin-top-2">
                                                                {Object.entries(item.selectedOptions).map(([k, v]) => `${k}: ${v}`).join(', ')}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="pcu-flex pcu-flex-column pcu-align-end">
                                                        <Text className="pcu-font-14 pcu-nearzon-orange pcu-font-800 pcu-white-space-nowrap" style={{ textDecoration }}>
                                                            {fmtPrice ? fmtPrice(displayPrice) : `₹${displayPrice}`}
                                                        </Text>
                                                        <Text className="pcu-font-10 pcu-color-muted pcu-font-600">Qty: {item.quantity}</Text>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            <Divider style={{ margin: '0 20px', width: 'auto', opacity: 0.5 }} />

                            {(order.items?.length > 1) && (
                                <div className="pcu-padding-20 pcu-flex-between pcu-flex-center-y pcu-bg-slate-50" style={{ borderRadius: '0 0 20px 20px' }}>
                                    <div className="pcu-flex pcu-flex-column">
                                        <Text type="secondary" className="pcu-font-13 pcu-font-700 pcu-text-uppercase pcu-letter-spacing-1">Order Summary</Text>
                                        <Text className="pcu-font-12 pcu-font-600 pcu-color-muted">
                                            Total Items: {order.items?.reduce((acc, item) => acc + (item.quantity || 0), 0)}
                                        </Text>
                                    </div>
                                    <div className="pcu-flex pcu-flex-column pcu-align-end">
                                        <Text type="secondary" className="pcu-font-13 pcu-font-700 pcu-text-uppercase pcu-letter-spacing-1">Total Amount</Text>
                                        <Text className="pcu-font-14 pcu-font-800 pcu-color-black" >
                                            {isSeller ? (
                                                fmtPrice 
                                                    ? fmtPrice(order.items?.reduce((acc, item) => acc + (item.price * (item.quantity || 0)), 0)) 
                                                    : `₹${order.items?.reduce((acc, item) => acc + (item.price * (item.quantity || 0)), 0)}`
                                            ) : (
                                                fmtPrice ? fmtPrice(order.totalAmount) : `₹${order.totalAmount}`
                                            )}
                                        </Text>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="pcu-padding-x-24 pcu-margin-top-12">
                {isSeller && activeOrders.length === 1 && (
                    <div className="pcu-bg-white pcu-padding-16 pcu-radius-20 pcu-border-slate-light pcu-margin-bottom-20 pcu-shadow-sm">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                            <div style={{ width: 3, height: 14, background: '#4f46e5', borderRadius: 2 }}></div>
                            <Text strong className="pcu-font-12 pcu-color-title pcu-text-uppercase pcu-letter-spacing-1">
                                Order Controls
                            </Text>
                        </div>

                        <div className="pcu-flex pcu-flex-column pcu-gap-8">
                            {/* Finalized Status Message */}
                            {['delivered', 'cancelled'].includes(activeOrders[0].status?.toLowerCase()) && (
                                <div className="pcu-bg-slate-50 pcu-padding-12 pcu-radius-12 pcu-border-slate-light pcu-flex pcu-flex-center-y pcu-gap-10">
                                    <CheckCircleOutlined style={{ color: activeOrders[0].status?.toLowerCase() === 'delivered' ? '#16a34a' : '#ef4444', fontSize: 16 }} />
                                    <Text className="pcu-font-12 pcu-font-600 pcu-color-muted">
                                        {activeOrders[0].status?.toLowerCase() === 'delivered'
                                            ? 'Order completed successfully. No further actions required.'
                                            : 'This order was cancelled. No further actions can be taken.'}
                                    </Text>
                                </div>
                            )}

                            {/* Primary Action Button - Dedicated Row */}
                            {activeOrders[0].status?.toLowerCase() === 'ordered' && (
                                <Button
                                    type="primary"
                                    icon={<CheckCircleOutlined />}
                                    className="pcu-bg-purple pcu-border-none pcu-radius-10 pcu-font-700 pcu-h-40"
                                    onClick={() => onStatusUpdate(activeOrders[0]._id, 'confirmed')}
                                    block
                                >
                                    Confirm Order
                                </Button>
                            )}
                            {activeOrders[0].status?.toLowerCase() === 'replaced' && (
                                <Button
                                    type="primary"
                                    icon={<CheckCircleOutlined />}
                                    className="pcu-bg-purple pcu-border-none pcu-radius-10 pcu-font-700 pcu-h-40"
                                    onClick={() => onConfirmReplaced && onConfirmReplaced(activeOrders[0]._id)}
                                    block
                                >
                                    Confirm Replaced Order
                                </Button>
                            )}
                            {['confirmed', 'assigned'].includes(activeOrders[0].status?.toLowerCase()) && (
                                <>
                                    <PackingTimer confirmedHistory={activeOrders[0].statusHistory?.find(h => h.status === 'confirmed' || h.status === 'CONFIRMED')} />
                                    <Button
                                        type="primary"
                                        icon={<GiftOutlined />}
                                        className="pcu-bg-orange pcu-border-none pcu-radius-10 pcu-font-700 pcu-h-40"
                                        onClick={() => onStatusUpdate(activeOrders[0]._id, 'packed')}
                                        block
                                    >
                                        Mark as Packed
                                    </Button>
                                </>
                            )}
                            {activeOrders[0].status?.toLowerCase() === 'assigned' && (
                                <div className="pcu-padding-12 pcu-radius-12 pcu-border-slate-light pcu-flex pcu-flex-column pcu-gap-10" style={{ background: '#f0f9ff', borderColor: '#bae6fd' }}>
                                    <div className="pcu-flex pcu-flex-center-y pcu-gap-10">
                                        <IconBike size={18} style={{ color: '#0369a1', flexShrink: 0 }} />
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Driver Assigned</span>
                                            <span style={{ fontSize: 12, color: '#64748b' }}>Delivery partner accepted. Awaiting packing.</span>
                                        </div>
                                    </div>
                                    {activeOrders[0].currentAssignedStaff && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4, padding: '10px 12px', background: '#fff', borderRadius: 8, border: '1px solid #e0f2fe' }}>
                                            <Avatar src={activeOrders[0].currentAssignedStaff.profilePhoto} icon={<UserOutlined />} size={40} style={{ border: '2px solid #bae6fd' }} />
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                                                    {activeOrders[0].currentAssignedStaff.firstName} {activeOrders[0].currentAssignedStaff.lastName}
                                                </div>
                                                <div style={{ fontSize: 12, color: '#475569', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <PhoneOutlined /> {activeOrders[0].currentAssignedStaff.phone}
                                                </div>
                                            </div>
                                            <a href={`tel:${activeOrders[0].currentAssignedStaff.phone}`} style={{ padding: '6px 12px', background: '#0284c7', color: '#fff', borderRadius: 6, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>Call</a>
                                        </div>
                                    )}
                                </div>
                            )}
                            {activeOrders[0].status?.toLowerCase() === 'packed' && (
                                <div className="pcu-padding-12 pcu-radius-12 pcu-bg-slate-50 pcu-border-slate-light pcu-flex pcu-flex-center-y pcu-gap-10">
                                    <IconBike size={18} style={{ color: '#0891b2' }} />
                                    <Text className="pcu-font-12 pcu-font-600 pcu-color-muted">
                                        Waiting for delivery partner to pick up the order.
                                    </Text>
                                </div>
                            )}

                            {/* Secondary Actions Grid */}
                            {!['delivered', 'cancelled', 'out_for_delivery', 'picked_up', 'shipped', 'packed'].includes(activeOrders[0].status?.toLowerCase()) && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, width: '100%' }}>
                                    <Button
                                        icon={<ClockCircleOutlined style={{ color: '#4f46e5' }} />}
                                        className="pcu-radius-10 pcu-font-700 pcu-h-38 pcu-border-slate-light"
                                        style={{ background: '#fff' }}
                                        onClick={() => onReschedule && onReschedule(activeOrders[0])}
                                    >
                                        Reschedule
                                    </Button>
                                    <Button
                                        icon={<IconAlertCircle size={16} style={{ color: '#f59e0b' }} />}
                                        className="pcu-radius-10 pcu-font-700 pcu-h-38 pcu-border-slate-light"
                                        style={{ background: '#fff' }}
                                        onClick={() => onRequestUpdate && onRequestUpdate(activeOrders[0])}
                                    >
                                        Order Issue Request
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="pcu-bg-muted pcu-padding-16 pcu-radius-12 pcu-border-slate">
                    <Text type="secondary" className="pcu-font-11 pcu-line-height-1-6 pcu-display-block">
                        <ClockCircleOutlined className="pcu-margin-right-8" />
                        {isSeller
                            ? 'Manage order status and issues from here. Buyer will be notified of any changes.'
                            : t('drawer_info_text', 'Showing items that are currently in progress. Once delivered, they will move to your order history.')
                        }
                    </Text>
                </div>
            </div>
        </Drawer>
    );
};

export default OrderStatusDrawer;
